import { useMemo, useCallback } from 'react';
import { includes } from 'lodash';
import moment from 'moment';

import {
  Nullable,
  SelectOption,
  SelectOptionWithImage,
} from '~globals/types/commons';
import {
  PlanFeaturesTypes,
  RouteItemStateType,
  CollectItemStateTypes,
  SourceImportType,
  PickingStateTypes,
} from '~globals/types/enums';
import { ItemFilterV2Base } from '~services/item/types';
import { VehicleBase } from '~services/vehicle/types';
import { DriverCompanyBase } from '~services/driver/types';
import { LogisticZoneBase } from '~services/logisticsZones/types';
import { RouteUserBasic } from '~services/route/types';
import { getItemValue } from '~utils/helpers';
import { formatterDate } from '~utils/formatter';

import { ItemsSearchFilters } from './useItemsSearchFilters';
import { useSubscriptionFeatures } from './useSubscriptionFeatures';

export const useParseFiltersRequestSearchItems = ({
  filters,
  activeFilterTabId,
  defaultRouteItemState,
  defaultCollectItemState,
  defaultGiverCollectItemStateTypesId,
}: {
  filters: ItemsSearchFilters;
  activeFilterTabId: string;
  defaultRouteItemState: RouteItemStateType[];
  defaultCollectItemState?: CollectItemStateTypes[];
  defaultGiverCollectItemStateTypesId?: CollectItemStateTypes[];
}): ((currentFilters?: Partial<ItemsSearchFilters>) => ItemFilterV2Base) => {
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const hasAccesLimitNotPro = useMemo(
    () => hasAccessToFeature(PlanFeaturesTypes.LimitNotPro),
    [hasAccessToFeature],
  );

  const hasAccesMgmZones = useMemo(
    () => hasAccessToFeature(PlanFeaturesTypes.MgmZones),
    [hasAccessToFeature],
  );

  const hasAccesLimitPremium = useMemo(
    () => hasAccessToFeature(PlanFeaturesTypes.LimitPremium),
    [hasAccessToFeature],
  );

  const validateFilterTabsId = useCallback(
    (currentFilterTabsId: string[]) =>
      includes(currentFilterTabsId, activeFilterTabId),
    [activeFilterTabId],
  );

  const getValueFilter = useCallback(
    <Key extends keyof ItemsSearchFilters>(key: Key): ItemsSearchFilters[Key] =>
      getItemValue(filters, key),
    [filters],
  );

  const parseSearch = useCallback(
    (currenSearch?: string): Nullable<string> => {
      let searchValue = getValueFilter('search');

      if (currenSearch !== undefined) {
        searchValue = currenSearch;
      }

      return searchValue || null;
    },
    [getValueFilter],
  );

  const parseDate = useCallback(
    ({
      currentKey,
      excludeFilterTabsId,
      currentDate,
    }: {
      currentKey:
        | 'expirationDateTimeFrom'
        | 'expirationDateTimeTo'
        | 'dateFrom'
        | 'dateTo'
        | 'finalizedDateTimeFrom'
        | 'finalizedDateTimeTo';
      excludeFilterTabsId?: string[];
      currentDate?: Nullable<Date>;
    }): Nullable<string> => {
      const isExcludeFilterTabsId = excludeFilterTabsId
        ? validateFilterTabsId(excludeFilterTabsId)
        : false;

      if (isExcludeFilterTabsId) return null;

      let dateValue = getValueFilter(currentKey);

      if (currentDate !== undefined) {
        dateValue = currentDate;
      }

      if (!dateValue) return null;

      const formatDate = formatterDate(dateValue, {
        format: moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
      });

      return formatDate || null;
    },
    [validateFilterTabsId, getValueFilter],
  );

  const parseIncludeWithoutMaxDeliveryDatetime = useCallback(
    (includeWithoutExpirationDate?: Nullable<boolean>): Nullable<boolean> => {
      let includeWithoutMaxDeliveryDatetime = getValueFilter(
        'includeWithoutExpirationDate',
      );

      if (includeWithoutExpirationDate !== undefined) {
        includeWithoutMaxDeliveryDatetime = includeWithoutExpirationDate;
      }

      return includeWithoutMaxDeliveryDatetime;
    },
    [getValueFilter],
  );

  const parseUnit = useCallback(
    ({
      currentKey,
      excludeFilterTabsId,
      currentUnit,
    }: {
      currentKey: 'unit1Gt' | 'unit1Lt' | 'unit2Gt' | 'unit2Lt';
      excludeFilterTabsId?: string[];
      currentUnit?: string;
    }): Nullable<number> => {
      if (!hasAccesLimitNotPro) return null;

      const isExcludeFilterTabsId = excludeFilterTabsId
        ? validateFilterTabsId(excludeFilterTabsId)
        : false;

      if (isExcludeFilterTabsId) return null;

      let unitValue = getValueFilter(currentKey);

      if (currentUnit !== undefined) {
        unitValue = currentUnit;
      }

      if (!unitValue) return null;

      return Number(unitValue);
    },
    [hasAccesLimitNotPro, validateFilterTabsId, getValueFilter],
  );

  const parsePrice = useCallback(
    ({
      currentKey,
      currentPrice,
    }: {
      currentKey: 'priceGt' | 'priceLt';
      currentPrice?: string;
    }): Nullable<number> => {
      let priceValue = getValueFilter(currentKey);

      if (currentPrice !== undefined) {
        priceValue = currentPrice;
      }

      if (!priceValue) return null;

      return Number(priceValue);
    },
    [getValueFilter],
  );

  const parseVehicles = useCallback(
    (currentVehicles?: Nullable<VehicleBase[]>): Nullable<string[]> => {
      if (validateFilterTabsId(['inWarehouse'])) return null;

      let vehiclesValue = getValueFilter('vehicles');

      if (currentVehicles !== undefined) {
        vehiclesValue = currentVehicles;
      }

      if (!vehiclesValue || vehiclesValue.length === 0) {
        return null;
      }

      return vehiclesValue.map((vehicle) => vehicle.id);
    },
    [validateFilterTabsId, getValueFilter],
  );

  const parseDrivers = useCallback(
    (currentDrivers?: Nullable<DriverCompanyBase[]>): Nullable<string[]> => {
      if (validateFilterTabsId(['inWarehouse'])) return null;

      let driversValue = getValueFilter('drivers');

      if (currentDrivers !== undefined) {
        driversValue = currentDrivers;
      }

      if (!driversValue || driversValue.length === 0) {
        return null;
      }

      return driversValue.map((driver) => driver.id);
    },
    [validateFilterTabsId, getValueFilter],
  );

  const parseServices = useCallback(
    (currentServices?: Nullable<SelectOption[]>): Nullable<string[]> => {
      if (!hasAccesLimitNotPro) return null;

      let servicesValue = getValueFilter('services');

      if (currentServices !== undefined) {
        servicesValue = currentServices;
      }

      if (!servicesValue || servicesValue.length === 0) {
        return null;
      }

      return servicesValue.map((service) => service.value.toString());
    },
    [hasAccesLimitNotPro, getValueFilter],
  );

  const parseZones = useCallback(
    (currentZones?: Nullable<LogisticZoneBase[]>): Nullable<string[]> => {
      if (!hasAccesMgmZones) return null;

      let zonesValue = getValueFilter('zones');

      if (currentZones !== undefined) {
        zonesValue = currentZones;
      }

      if (!zonesValue || zonesValue.length === 0) {
        return null;
      }

      return zonesValue.map((zone) => zone.zoneId);
    },
    [hasAccesMgmZones, getValueFilter],
  );

  const parseAssignedUser = useCallback(
    (currentAssignedUser?: Nullable<RouteUserBasic>): Nullable<string> => {
      if (!hasAccesLimitNotPro) return null;

      let assignedUserValue = getValueFilter('assignedUser');

      if (currentAssignedUser !== undefined) {
        assignedUserValue = currentAssignedUser;
      }

      return assignedUserValue?.userId ?? null;
    },
    [hasAccesLimitNotPro, getValueFilter],
  );

  const parseSourceImports = useCallback(
    (
      currentSourceImports?: Nullable<SelectOptionWithImage[]>,
    ): Nullable<SourceImportType[]> => {
      let sourceImportsValue = getValueFilter('sourceImports');

      if (currentSourceImports !== undefined) {
        sourceImportsValue = currentSourceImports;
      }

      if (!sourceImportsValue || sourceImportsValue.length === 0) {
        return null;
      }

      return sourceImportsValue.map((sourceImport) =>
        Number(sourceImport.value),
      );
    },
    [getValueFilter],
  );

  const parsePickingState = useCallback(
    (
      currentPickingState?: Nullable<SelectOption>,
    ): Nullable<PickingStateTypes> => {
      if (!hasAccesLimitPremium) return null;

      if (validateFilterTabsId(['inTransit'])) return null;

      let pickingStateValue = getValueFilter('pickingState');

      if (currentPickingState !== undefined) {
        pickingStateValue = currentPickingState;
      }

      if (!pickingStateValue) return null;

      return Number(pickingStateValue.value);
    },
    [validateFilterTabsId, hasAccesLimitPremium, getValueFilter],
  );

  const parseRouteItemStates = useCallback(
    (currentItemtState?: Nullable<SelectOption[]>): RouteItemStateType[] => {
      let routeItemStateValue = getValueFilter('itemState');

      if (currentItemtState !== undefined) {
        routeItemStateValue = currentItemtState;
      }

      if (!routeItemStateValue || routeItemStateValue.length === 0) {
        return defaultRouteItemState;
      }

      return routeItemStateValue.map((routeItemState) =>
        Number(routeItemState.value),
      );
    },
    [getValueFilter, defaultRouteItemState],
  );

  const parsCollectItemStates = useCallback(
    (
      currentCollectState?: Nullable<SelectOption[]>,
    ): CollectItemStateTypes[] => {
      let collectItemStateValue = getValueFilter('collectState');

      if (currentCollectState !== undefined) {
        collectItemStateValue = currentCollectState;
      }

      if (!collectItemStateValue || collectItemStateValue.length === 0) {
        return defaultCollectItemState ?? [];
      }

      return collectItemStateValue.map((collectItemState) =>
        Number(collectItemState.value),
      );
    },
    [getValueFilter, defaultCollectItemState],
  );

  const parsGiverCollectItemStates = useCallback(
    (
      currentGiverCollectState?: Nullable<SelectOption[]>,
    ): CollectItemStateTypes[] => {
      let giverCollectItemStateValue = getValueFilter('giverCollectState');

      if (currentGiverCollectState !== undefined) {
        giverCollectItemStateValue = currentGiverCollectState;
      }

      if (
        !giverCollectItemStateValue ||
        giverCollectItemStateValue.length === 0
      ) {
        return defaultGiverCollectItemStateTypesId ?? [];
      }

      return giverCollectItemStateValue.map((giverCollectItemState) =>
        Number(giverCollectItemState.value),
      );
    },
    [getValueFilter, defaultGiverCollectItemStateTypesId],
  );

  const parseFiltersRequest = useCallback(
    (currentFilters?: Partial<ItemsSearchFilters>): ItemFilterV2Base => ({
      routeCode: parseSearch(currentFilters?.search),
      maxDeliveredDateTimeFrom: parseDate({
        currentKey: 'expirationDateTimeFrom',
        currentDate: currentFilters?.expirationDateTimeFrom,
      }),
      maxDeliveredDateTimeTo: parseDate({
        currentKey: 'expirationDateTimeTo',
        currentDate: currentFilters?.expirationDateTimeTo,
      }),
      includeWithoutMaxDeliveryDatetime: parseIncludeWithoutMaxDeliveryDatetime(
        currentFilters?.includeWithoutExpirationDate,
      ),
      dateFrom: parseDate({
        currentKey: 'dateFrom',
        currentDate: currentFilters?.dateFrom,
        excludeFilterTabsId: ['inWarehouse'],
      }),
      finalizedDateTimeFrom: parseDate({
        currentKey: 'finalizedDateTimeFrom',
        currentDate: currentFilters?.finalizedDateTimeFrom,
        excludeFilterTabsId: ['inWarehouse', 'inTransit'],
      }),
      finalizedDateTimeTo: parseDate({
        currentKey: 'finalizedDateTimeTo',
        currentDate: currentFilters?.finalizedDateTimeTo,
        excludeFilterTabsId: ['inWarehouse', 'inTransit'],
      }),
      dateTo: parseDate({
        currentKey: 'dateTo',
        currentDate: currentFilters?.dateTo,
        excludeFilterTabsId: ['inWarehouse'],
      }),
      unit1GraterThan: parseUnit({
        currentKey: 'unit1Gt',
        currentUnit: currentFilters?.unit1Gt,
        excludeFilterTabsId: ['inTransit', 'finalized'],
      }),
      unit1LowerThan: parseUnit({
        currentKey: 'unit1Lt',
        currentUnit: currentFilters?.unit1Lt,
        excludeFilterTabsId: ['inTransit', 'finalized'],
      }),
      unit2GraterThan: parseUnit({
        currentKey: 'unit2Gt',
        currentUnit: currentFilters?.unit2Gt,
        excludeFilterTabsId: ['inTransit', 'finalized'],
      }),
      unit2LowerThan: parseUnit({
        currentKey: 'unit2Lt',
        currentUnit: currentFilters?.unit2Lt,
        excludeFilterTabsId: ['inTransit', 'finalized'],
      }),
      priceGraterThan: parsePrice({
        currentKey: 'priceGt',
        currentPrice: currentFilters?.priceGt,
      }),
      priceLowerThan: parsePrice({
        currentKey: 'priceLt',
        currentPrice: currentFilters?.priceLt,
      }),
      vehiclesId: parseVehicles(currentFilters?.vehicles),
      driversId: parseDrivers(currentFilters?.drivers),
      serviceTypeList: parseServices(currentFilters?.services),
      zonesId: parseZones(currentFilters?.zones),
      assignedUserId: parseAssignedUser(currentFilters?.assignedUser),
      sourceImportTypeIds: parseSourceImports(currentFilters?.sourceImports),
      routeItemStateTypesId: parseRouteItemStates(currentFilters?.itemState),
      pickingStateTypeId: parsePickingState(currentFilters?.pickingState),
      // Default off; the In-Warehouse tab context overrides this via a wrapper.
      includeCollectedItems: null,
      collectItemStateTypesId: parsCollectItemStates(
        currentFilters?.collectState,
      ),
      giverCollectItemStateTypesId: parsGiverCollectItemStates(
        currentFilters?.giverCollectState,
      ),
    }),
    [
      parseSearch,
      parseDate,
      parseIncludeWithoutMaxDeliveryDatetime,
      parseUnit,
      parsePrice,
      parseVehicles,
      parseDrivers,
      parseServices,
      parseZones,
      parseAssignedUser,
      parseSourceImports,
      parseRouteItemStates,
      parsePickingState,
      parsCollectItemStates,
      parsGiverCollectItemStates,
    ],
  );

  return parseFiltersRequest;
};
